/**
 * Offline credential cache using PBKDF2 password hashing.
 *
 * On successful online login, the password hash is stored in IndexedDB.
 * When offline, the user can re-authenticate by verifying their password
 * against the cached hash — no network required.
 *
 * Security:
 * - PBKDF2 with 100k iterations, SHA-256, 256-bit output
 * - Per-user random salt (32 bytes)
 * - Hash expires after `offlineLoginMaxAgeHours` (configurable via platform policy)
 * - Cleared on explicit sign-out
 */

import { db } from '../offline/db';

/**
 * Generate a cryptographically random salt.
 */
function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Hash a password using PBKDF2 (Web Crypto API).
 */
async function pbkdf2Hash(password: string, salt: Uint8Array): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(password);
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoded.buffer as ArrayBuffer,
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  return crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: 100_000,
      hash: 'SHA-256',
    },
    keyMaterial,
    256,
  );
}

function arrayBufferToBase64(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}

function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Store a password hash for offline credential verification.
 * Called after successful online login.
 */
export async function storeCredentialHash(
  email: string,
  password: string,
  userId: string,
): Promise<void> {
  const salt = generateSalt();
  const hashBuffer = await pbkdf2Hash(password, salt);
  const hash = arrayBufferToBase64(hashBuffer);
  const saltBase64 = arrayBufferToBase64(salt);

  // Upsert — replace existing entry for this email
  const existing = await db.credentialCache.where('email').equals(email).first();
  if (existing?.id != null) {
    await db.credentialCache.update(existing.id, {
      hash,
      salt: saltBase64,
      storedAt: Date.now(),
      userId,
    });
  } else {
    await db.credentialCache.add({
      email,
      hash,
      salt: saltBase64,
      storedAt: Date.now(),
      userId,
    });
  }
}

/**
 * Verify a password against the cached credential hash.
 * Returns the userId if verification succeeds, null otherwise.
 */
export async function verifyOfflineCredential(
  email: string,
  password: string,
): Promise<string | null> {
  const entry = await db.credentialCache.where('email').equals(email).first();
  if (!entry) return null;

  const salt = base64ToUint8Array(entry.salt);
  const hashBuffer = await pbkdf2Hash(password, new Uint8Array(salt));
  const hash = arrayBufferToBase64(hashBuffer);

  if (hash !== entry.hash) return null;
  return entry.userId;
}

/**
 * Check if the cached credential is still valid (not expired).
 */
export async function isOfflineLoginValid(
  email: string,
  maxAgeHours: number,
): Promise<boolean> {
  const entry = await db.credentialCache.where('email').equals(email).first();
  if (!entry) return false;

  const ageMs = Date.now() - entry.storedAt;
  const maxAgeMs = maxAgeHours * 3600_000;
  return ageMs <= maxAgeMs;
}

/**
 * Clear cached credentials for a specific email (on explicit sign-out).
 */
export async function clearCredentialHash(email: string): Promise<void> {
  await db.credentialCache.where('email').equals(email).delete();
}

/**
 * Clear all cached credentials (nuclear option — zoo switch).
 */
export async function clearAllCredentials(): Promise<void> {
  await db.credentialCache.clear();
}
